import type { DecoratedTask } from '@witmetrics/utils';
import {
  currentDate,
  differenceInDays,
  formatDateOnlyTime,
  formatTextDate,
  getStartOfDay,
  isPast,
  isToday,
  isTomorrow,
} from '@witmetrics/utils/time';

export const classes = {
  wrapper: 'flex shrink-0 items-center gap-1.5',
  dot: 'size-1.5 shrink-0 rounded-full',
  label: 'whitespace-nowrap text-xs font-semibold tracking-wide',
  overdueText: 'text-[#ef4444]',
  overdueDot: 'bg-[#ef4444]',
  soonText: 'text-[#f59e0b]',
  soonDot: 'bg-[#f59e0b]',
  futureText: 'text-[#0fb5c4]',
  futureDot: 'bg-[#0fb5c4]',
  resolvedText: 'text-[#8a909c] line-through',
};

type DueDisplayOptions = {
  onlyTime?: boolean;
  dateFormat?: Parameters<typeof formatTextDate>[1];
  timeFormat?: Parameters<typeof formatDateOnlyTime>[1];
};

export function getDueDisplay(
  task: DecoratedTask,
  { onlyTime, dateFormat, timeFormat }: DueDisplayOptions
) {
  const time = formatDateOnlyTime(task.dueAt, timeFormat);
  if (task.isResolved) {
    return {
      label: time,
      textClass: classes.resolvedText,
      dotClass: undefined,
    };
  }
  if (isToday(task.dueAt)) {
    return {
      label: onlyTime ? time : `Today · ${time}`,
      textClass: classes.soonText,
      dotClass: classes.soonDot,
    };
  }
  if (isTomorrow(task.dueAt)) {
    return {
      label: onlyTime ? time : `Tomorrow · ${time}`,
      textClass: classes.soonText,
      dotClass: classes.soonDot,
    };
  }
  if (isPast(task.dueAt)) {
    const days = differenceInDays(
      getStartOfDay(task.dueAt),
      getStartOfDay(currentDate())
    );
    return {
      label: onlyTime ? time : `Overdue · ${days}d`,
      textClass: classes.overdueText,
      dotClass: classes.overdueDot,
    };
  }
  return {
    label: onlyTime
      ? time
      : `${formatTextDate(task.dueAt, dateFormat, true)} · ${time}`,
    textClass: classes.futureText,
    dotClass: classes.futureDot,
  };
}
